"use client";

import { useMutation } from "@apollo/client";
import {
	faArrowUpRightFromSquare,
	faCheck,
	faChevronRight,
	faEnvelope,
	faRotate,
	faRotateRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	type ContactInput,
	SendContactDocument,
	type SendContactMutation,
	type SendContactMutationVariables,
} from "@graphql";
import { Alert, Button, Form, Typography, useNotifications, useZodForm } from "@tosize/ui";
import { RadioGroup } from "radix-ui";
import { Fragment, type ReactNode, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { z } from "zod";
import type { ContactOrdersType } from "~/app/actions/customer";
import { postProblem } from "~/app/api/hemlock";
import { StyledLink } from "~/components/Link";
import { useQuery as useRouteQuery } from "~/hooks/useQuery";
import { mapValidationErrorsToForm } from "~/lib/utils";
import {
	type ContactCategory,
	type ContactSubject,
	getCategorySubjects,
	getSchema,
} from "./types/schema";

interface EditDetailsFormProps {
	lang: string;
	children: ReactNode;
	customer: ContactOrdersType;
	messages: {
		success: string;
		error: string;
		reset: string;
		retry: string;
		email?: string;
	};
}

export type HemlockContactFormType = z.infer<Awaited<ReturnType<typeof getSchema>>>;

export function HemlockContactForm({ lang, children, customer, messages }: EditDetailsFormProps) {
	const { router, searchParams } = useRouteQuery();
	const { notify } = useNotifications();
	const categories = getCategorySubjects(lang);

	const categoryParam = (searchParams.get("category") ?? undefined) as ContactCategory | undefined;
	const subjectParam = (searchParams.get("subject") ?? undefined) as ContactSubject | undefined;
	const currentSubject = categoryParam ? categories[categoryParam] : undefined;
	const subject = Array.isArray(currentSubject) ? subjectParam : currentSubject;

	const emailParam = searchParams.get("email") ?? undefined;
	const incrementIdParam = searchParams.get("increment_id") ?? undefined;
	const defaultValues =
		subject === "order_product_complaint" || subject === "order_product_not_delivered"
			? {
					email: emailParam ?? customer?.orders?.items.at(0)?.email ?? customer?.email,
					increment_id: incrementIdParam ?? customer?.orders?.items.at(0)?.incrementId,
				}
			: {
					email: emailParam ?? customer?.email,
					phone: incrementIdParam ?? customer?.phone ?? undefined,
				};

	const form = useZodForm({
		schema: getSchema(subject),
		defaultValues: {
			category: categoryParam,
			subject,
			...defaultValues,
		},
	});

	const {
		reset,
		setError,
		formState: { errors },
	} = form;

	const [state, setState] = useState<{ type: "error" | "success"; message?: string }>();
	const [sendContact, { reset: resetSendContact }] = useMutation<
		SendContactMutation,
		SendContactMutationVariables
	>(SendContactDocument, { context: { clientName: "magento_upload" } });

	if (state) {
		return (
			<div className="flex flex-col gap-4">
				<Alert variant={state.type === "error" ? "danger" : "success"}>
					{messages[state.type]}
				</Alert>

				{!!state.message && (
					<Typography className="text-center">
						<i>- {state.message} -</i>
					</Typography>
				)}

				{state.type === "error" ? (
					<>
						<Button onClick={() => setState(undefined)} starticon={faRotateRight}>
							{messages.retry}
						</Button>

						<StyledLink variant="outlined" starticon={faEnvelope} href={`mailto:${messages.email}`}>
							{messages.email}
						</StyledLink>
					</>
				) : (
					<Button
						type="reset"
						onClick={() => {
							resetSendContact();
							reset(defaultValues);
							setState(undefined);
						}}
						variant="outlined"
						starticon={faRotate}
					>
						{messages.reset}
					</Button>
				)}
			</div>
		);
	}

	return (
		<Form
			className="flex flex-col gap-12"
			form={form}
			onSubmit={async (input) => {
				if (!Object.values(errors).length) {
					const isHemlock =
						input.subject === "order_product_complaint" ||
						input.subject === "order_product_not_delivered";

					if (isHemlock) {
						try {
							const response = await postProblem({
								email: input.email,
								increment_id: input.increment_id,
								items: input.product
									.filter((product) => !!product.issue?.type && !!product.solution?.type)
									.map((product) => ({
										form_type: input.subject,
										order_item_id: product.order_item_id,
										panel_id: product.panel_id,
										problem_category_id: product.issue?.sub_type ?? product.issue?.type ?? "",
										solution_category: product.solution?.type ?? "",
										images: product.files ?? [],
										comment: [product.issue?.description, product.solution?.description]
											.filter(Boolean)
											.join("\n"),
									})),
							});

							if (!response || "errors" in response) {
								setState({ type: "error", message: response?.message });
								notify({
									variant: "danger",
									title: messages.error,
									subtitle: response?.message,
								});
							} else {
								setState({ type: "success" });

								notify({
									variant: "success",
									title: messages.success,
								});
							}

							router.push("#main-content");
						} catch (error) {
							mapValidationErrorsToForm(error, setError);
							setState({
								type: "error",
								message: error instanceof Error ? error.message : undefined,
							});
							notify({
								variant: "danger",
								title: messages.error,
							});
						}
					} else {
						try {
							const { category: _category, subject: _subject, ...sendContactInput } = input;
							const sendContactData = await sendContact({
								variables: { input: sendContactInput as ContactInput },
							});

							setState({
								type: sendContactData.data?.sendContact?.success ? "success" : "error",
								message: sendContactData.data?.sendContact?.message,
							});

							notify({
								variant: sendContactData.data?.sendContact?.success ? "success" : "danger",
								title: sendContactData.data?.sendContact?.message ?? "",
							});
							if (sendContactData.data?.sendContact?.success) {
								router.push("#main-content");
							}
						} catch (error) {
							setState({
								type: "error",
								message: error instanceof Error ? error.message : undefined,
							});
							mapValidationErrorsToForm(error, setError);
						}
					}
				}
			}}
		>
			{children}
		</Form>
	);
}

type Subjects = Partial<Record<ContactSubject, { title: string; subtitle: string }>>;

interface CategorySubjectSelectorProps {
	lang: string;
	customer: ContactOrdersType;
	messages: {
		selected: string;
		categories: Record<ContactCategory, { title: string; subtitle?: string; subjects?: Subjects }>;
	};
}

export function CategorySubjectSelector(props: CategorySubjectSelectorProps) {
	const { lang, customer, messages } = props;
	const { createUrl, pathname, searchParams, router } = useRouteQuery();
	const categories = getCategorySubjects(lang);
	const { register, watch, reset } = useFormContext();
	const categoryValue: unknown = watch("category");
	const subjectValue: unknown = watch("subject");
	const emailValue: unknown = watch("email");
	const incrementIdValue: unknown = watch("increment_id");

	const selected =
		typeof categoryValue === "string" ? (categoryValue as ContactCategory) : undefined;
	const currentSubjects = selected ? categories[selected] : undefined;

	const getDefaultValues = useCallback(
		(subject: string) => {
			const email = typeof emailValue === "string" ? emailValue : undefined;
			const incrementId = typeof incrementIdValue === "string" ? incrementIdValue : undefined;

			return subject === "order_product_complaint" || subject === "order_product_not_delivered"
				? {
						email: email ?? customer?.orders?.items.at(0)?.email ?? customer?.email,
						increment_id: incrementId ?? customer?.orders?.items.at(0)?.incrementId,
					}
				: {
						email: email ?? customer?.email,
						phone: incrementId ?? customer?.phone ?? undefined,
					};
		},
		[emailValue, incrementIdValue, customer],
	);

	const scroll = useCallback((indicator = "#contact-form") => {
		const element = document.querySelector(indicator);
		element?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
	}, []);

	const onCategoryChange = useCallback(
		async (newValue: string) => {
			const currentSubject = categories[newValue as ContactCategory];
			const subject = Array.isArray(currentSubject) ? undefined : currentSubject;
			const defaultValues = subject ? getDefaultValues(subject) : undefined;

			reset({
				category: newValue,
				subject: subject ?? null,
				...defaultValues,
			});

			if (pathname?.startsWith("/contacts")) {
				window.history.pushState(
					null,
					"",
					createUrl({
						category: newValue,
						subject: subject ?? null,
						product_type: null,
						email: null,
						increment_id: null,
					}),
				);

				await new Promise((resolve) => setTimeout(resolve, 100));
				const indicator = Array.isArray(currentSubject) ? "#contact-subjects" : "#contact-form";
				scroll(indicator);
			}
		},
		[categories, getDefaultValues, pathname, reset, createUrl, scroll],
	);

	const onSubjectChange = useCallback(
		async (newValue: string, newSubject: string, newProductType?: string) => {
			const defaultValues = getDefaultValues(newSubject);
			const isHemlock =
				newSubject === "order_product_complaint" || newSubject === "order_product_not_delivered";

			reset({
				category: newValue,
				subject: newSubject,
				product_type: newProductType ?? null,
				...defaultValues,
			});

			if (newSubject === "order_change") {
				return router.push("/order/edit");
			}

			if (pathname?.startsWith("/contacts")) {
				window.history.pushState(
					null,
					"",
					createUrl({
						category: newValue,
						subject: newSubject,
						product_type: newProductType ?? null,
						email: isHemlock ? (defaultValues?.email ?? null) : null,
						increment_id: isHemlock ? (defaultValues?.increment_id ?? null) : null,
					}),
				);

				await new Promise((resolve) => setTimeout(resolve, 100));
				scroll();
			}
		},
		[getDefaultValues, pathname, reset, createUrl, router, scroll],
	);

	useEffect(() => {
		const categoryParam = searchParams.get("category");
		const subjectParam = searchParams.get("subject");
		const productTypeParam = searchParams.get("product_type");

		if (categoryParam !== null) {
			if (subjectParam !== null && subjectParam !== subjectValue) {
				onSubjectChange(categoryParam, subjectParam, productTypeParam ?? undefined).catch(
					() => null,
				);
			} else if (categoryParam !== categoryValue) {
				onCategoryChange(categoryParam).catch(() => null);
			}
		}
	}, [searchParams, categoryValue, subjectValue, onCategoryChange, onSubjectChange]);

	return (
		<Fragment>
			<input type="hidden" {...register("category")} />
			<RadioGroup.Root
				className="grid grid-cols-1 xs:grid-cols-2 gap-4 sm:grid-cols-3"
				value={typeof categoryValue === "string" ? categoryValue : undefined}
				onValueChange={onCategoryChange}
			>
				{Object.keys(categories).map((category, index) => (
					<RadioGroup.Item
						key={index}
						value={category}
						title={messages.categories[category as ContactCategory].title}
						onClick={async () => await onCategoryChange(category)}
						className="flex items-center justify-center rounded border border-shades rdx-state-checked:border-primary-medium p-2 text-primary-medium leading-snug rdx-state-checked:ring-1 rdx-state-checked:ring-primary-medium rdx-state-checked:ring-inset rdx-state-checked:ring-offset-1 hover:border-primary-medium focus:outline-none motion-safe:transition-colors sm:p-4"
					>
						{messages.categories[category as ContactCategory].title}
					</RadioGroup.Item>
				))}
			</RadioGroup.Root>

			{selected && Array.isArray(currentSubjects) && (
				<div>
					<div className="target mb-4 flex flex-col items-center text-center" id="contact-subjects">
						<Typography variant="h2" color="text-primary-medium">
							{messages.categories[selected].title}
						</Typography>

						{!!messages.categories[selected].subtitle && (
							<Typography>{messages.categories[selected].subtitle}</Typography>
						)}
					</div>

					<input type="hidden" {...register("subject")} />

					<RadioGroup.Root
						className="mx-auto flex flex-col divide-y divide-primary-medium"
						value={typeof subjectValue === "string" ? subjectValue : undefined}
						onValueChange={(value) => onSubjectChange(selected, value)}
					>
						{currentSubjects.map((subject, index) => (
							<RadioGroup.Item
								key={index}
								value={subject}
								title={messages.categories[selected].subjects?.[subject]?.title}
								onClick={() => onSubjectChange(selected, subject)}
								className="group flex appearance-none items-center gap-8 py-4 first-of-type:pt-0 last-of-type:pb-0 focus:outline-none"
							>
								<div className="flex flex-col gap-1">
									<div className="flex flex-wrap gap-x-4 gap-y-2 text-left">
										<Typography
											variant="paragraph-bold"
											size="text-xl"
											className="group-enter:underline"
										>
											{messages.categories[selected].subjects?.[subject]?.title}
										</Typography>

										{subject !== "order_change" && subject === subjectValue && (
											<div className="flex items-center whitespace-nowrap rounded-full bg-shades-light px-3 py-1 font-bold text-primary-medium text-sm">
												<FontAwesomeIcon className="mr-2 h-3 w-3" icon={faCheck} />
												{messages.selected}
											</div>
										)}
									</div>
									<Typography className="text-left" variant="paragraph-italic">
										{messages.categories[selected].subjects?.[subject]?.subtitle}
									</Typography>
								</div>
								<FontAwesomeIcon
									icon={subject === "order_change" ? faArrowUpRightFromSquare : faChevronRight}
									size="1x"
									className="ml-auto self-center"
								/>
							</RadioGroup.Item>
						))}
					</RadioGroup.Root>
				</div>
			)}
		</Fragment>
	);
}
